import { MinigridLiveSnapshot } from "../../../../types/minigridtypes";
import { LiveEnergySegment } from "../../../../liveEnergyDashboardConfig";
import {
    SolarIcon,
    GridIcon,
    GeneratorIcon,
    ConsumptionNodeIcon,
} from "../asset/icon";
import styles from "./FlowDiagram.module.scss";

export interface FlowDiagramProps {
    snapshot: MinigridLiveSnapshot | null;
    onNodeNavigate?: (segment: LiveEnergySegment) => void;
}

/* ── SVG layout constants (viewBox 800 x 600) ─────────────────────
   NB : left/right/center reflètent les positions CSS des cartes
   (12% / 50% / 88% du container). Les offsets ±70 placent le début
   des chemins juste sous l'arête des cartes (z-index:1 les masque). */
const LAYOUT = {
    left: 96,
    center: 400,
    right: 704,
    rowSolar: 90,
    rowGrid: 300,
    rowGen: 510,
    rowBattery: 300,
    rowConso: 300,
};

type Flow = {
    id: string;
    path: string;
    labelX: number;
    labelY: number;
    active: boolean;
    powerW: number;
    color: string;
    labelText: string;
    reversed?: boolean;
};

export default function FlowDiagram({ snapshot, onNodeNavigate }: FlowDiagramProps) {
    const solar = snapshot?.solar_power_w ?? 0;
    // Consommation = compteur Schneider aval (active_power_total). Fallback CCGX pour compat.
    const consumption = snapshot?.schneider_consumption_w ?? snapshot?.consumption_w ?? 0;
    // Le réseau reste dans les sources mais n'est pas alimenté par Schneider.
    const grid = 0;
    const battery = snapshot?.battery_power_w ?? 0;
    const soc = snapshot?.battery_soc_percent ?? 0;
    const generator = Math.max(0, snapshot?.generator_power_w ?? 0);

    const solarActive = solar > 0;
    const gridImporting = grid > 0;
    const gridExporting = grid < 0;
    const generatorActive = generator > 0;
    const batteryCharging = battery > 0;
    const batteryDischarging = battery < 0;
    const consumptionActive = consumption > 0;

    const batteryLow = soc < 20;
    const batteryCriticalDischarge = batteryLow && batteryDischarging;

    const flows: Flow[] = [
        {
            id: "solar",
            path: `M ${LAYOUT.left + 70} ${LAYOUT.rowSolar} C 280 ${LAYOUT.rowSolar}, 340 ${LAYOUT.rowBattery - 30}, ${LAYOUT.center - 60} ${LAYOUT.rowBattery}`,
            labelX: 280,
            labelY: LAYOUT.rowSolar - 18,
            active: solarActive,
            powerW: solar,
            color: "var(--flow-solar)",
            labelText: "Solaire",
        },
        {
            id: "grid",
            path: `M ${LAYOUT.left + 70} ${LAYOUT.rowGrid} L ${LAYOUT.center - 60} ${LAYOUT.rowBattery}`,
            labelX: (LAYOUT.left + 70 + LAYOUT.center - 60) / 2,
            labelY: LAYOUT.rowGrid - 14,
            active: gridImporting || gridExporting,
            powerW: Math.abs(grid),
            color: "var(--flow-grid)",
            labelText: gridExporting ? "Export" : "Import",
            reversed: gridExporting,
        },
        {
            id: "generator",
            path: `M ${LAYOUT.left + 70} ${LAYOUT.rowGen} C 280 ${LAYOUT.rowGen}, 340 ${LAYOUT.rowBattery + 30}, ${LAYOUT.center - 60} ${LAYOUT.rowBattery}`,
            labelX: 280,
            labelY: LAYOUT.rowGen + 22,
            active: generatorActive,
            powerW: generator,
            color: "var(--flow-generator)",
            labelText: "Groupe",
        },
        {
            id: "conso",
            path: `M ${LAYOUT.center + 60} ${LAYOUT.rowBattery} L ${LAYOUT.right - 70} ${LAYOUT.rowConso}`,
            labelX: (LAYOUT.center + 60 + LAYOUT.right - 70) / 2,
            labelY: LAYOUT.rowBattery - 14,
            active: consumptionActive,
            powerW: consumption,
            color: "var(--flow-conso)",
            labelText: "Conso",
        },
    ];

    return (
        <div className={styles.container}>
            <svg
                className={styles.svg}
                viewBox="0 0 800 600"
                preserveAspectRatio="xMidYMid meet"
            >
                {flows.map((f) => (
                    <FlowPath key={f.id} flow={f} />
                ))}
            </svg>

            {/* ── Sources (left column) ── */}
            <NodeCard
                cls={`${styles.node} ${styles.nodeSolar}`}
                active={solarActive}
                onClick={() => onNodeNavigate?.("solar")}
                aria-label="Ouvrir le tableau de bord Solaire"
            >
                <div className={styles.nodeIconWrap}>
                    <SolarIcon className={styles.icon} />
                </div>
                <span className={styles.nodeName}>Solaire</span>
                <span className={styles.nodeValue}>{fmtPower(solar)}</span>
            </NodeCard>

            <NodeCard
                cls={`${styles.node} ${styles.nodeGrid}`}
                active={gridImporting || gridExporting}
                onClick={undefined}
                aria-label="Réseau"
            >
                <div className={styles.nodeIconWrap}>
                    <GridIcon className={styles.icon} />
                </div>
                <span className={styles.nodeName}>Réseau</span>
                <span className={styles.nodeValue}>{fmtPower(grid)}</span>
                <span className={styles.nodeSub}>
                    {gridImporting ? "Import" : gridExporting ? "Export" : "Neutre"}
                </span>
            </NodeCard>

            <NodeCard
                cls={`${styles.node} ${styles.nodeGenerator} ${generatorActive ? styles.nodeGeneratorActive : ""}`}
                active={generatorActive}
                onClick={() => onNodeNavigate?.("generator")}
                aria-label="Ouvrir le tableau de bord Groupe électrogène"
            >
                <div className={styles.nodeIconWrap}>
                    <GeneratorIcon className={styles.icon} />
                </div>
                <span className={styles.nodeName}>Groupe électrogène</span>
                <span className={styles.nodeValue}>{fmtPower(generator)}</span>
            </NodeCard>

            {/* ── Battery (center) ── */}
            <NodeCard
                cls={`${styles.node} ${styles.nodeBattery} ${batteryCriticalDischarge ? styles.nodeBatteryCritical : batteryLow ? styles.nodeBatteryLow : ""}`}
                active
                onClick={() => onNodeNavigate?.("battery")}
                aria-label="Ouvrir le tableau de bord Batterie"
            >
                <div className={styles.nodeIconWrap}>
                    <BatterySoc
                        soc={soc}
                        charging={batteryCharging}
                        discharging={batteryDischarging}
                    />
                </div>
                <span className={styles.nodeName}>Batterie</span>
                <span className={styles.nodeValue}>{soc}%</span>
                <span className={styles.nodeSub}>
                    {batteryCharging
                        ? `Charge • ${fmtPower(battery)}`
                        : batteryDischarging
                            ? `Décharge • ${fmtPower(battery)}`
                            : "Stable"}
                </span>
            </NodeCard>

            {/* ── Consumption (right) ── */}
            <NodeCard
                cls={`${styles.node} ${styles.nodeConso}`}
                active={consumptionActive}
                onClick={() => onNodeNavigate?.("consumption")}
                aria-label="Ouvrir le tableau de bord Consommation"
            >
                <div className={styles.nodeIconWrap}>
                    <ConsumptionNodeIcon className={styles.icon} />
                </div>
                <span className={styles.nodeName}>Consommation</span>
                <span className={styles.nodeValue}>{fmtPower(consumption)}</span>
            </NodeCard>
        </div>
    );
}

/* ── Flow animated path ─────────────────────────────────────────── */
function FlowPath({ flow }: { flow: Flow }) {
    const strokeWidth = flow.active ? powerToStrokeWidth(flow.powerW) : 1.5;
    const baseClass = `${styles.flowPath} ${flow.active ? styles.flowPathActive : styles.flowPathIdle} ${flow.reversed ? styles.flowPathReversed : ""}`;

    return (
        <g>
            <path
                d={flow.path}
                fill="none"
                stroke={flow.active ? flow.color : "var(--flow-idle)"}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                className={`${styles.flowBase} ${flow.active ? styles.flowBaseActive : ""}`}
            />
            {flow.active && (
                <path
                    d={flow.path}
                    fill="none"
                    stroke={flow.color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray="8 14"
                    className={baseClass}
                />
            )}
            {flow.active && (
                <g className={styles.flowLabel}>
                    <rect
                        x={flow.labelX - 34}
                        y={flow.labelY - 11}
                        width="68"
                        height="22"
                        rx="11"
                        className={styles.flowLabelBg}
                    />
                    <text
                        x={flow.labelX}
                        y={flow.labelY + 4}
                        textAnchor="middle"
                        className={styles.flowLabelText}
                    >
                        {fmtPower(flow.powerW)}
                    </text>
                </g>
            )}
        </g>
    );
}

/* ── Battery SOC filled icon ─────────────────────────────────────── */
function BatterySoc({
    soc,
    charging,
    discharging,
}: {
    soc: number;
    charging: boolean;
    discharging: boolean;
}) {
    const clamped = Math.max(0, Math.min(100, soc));
    // inner rect: x=8 y=9 w=16 h=16 — rail inside battery body
    const maxH = 14;
    const fillH = (clamped / 100) * maxH;
    const fillY = 9 + 1 + (maxH - fillH);

    const fillColor = clamped < 20 ? "var(--battery-critical)" : "var(--battery-ok)";

    return (
        <svg className={styles.icon} viewBox="0 0 32 32" fill="none">
            {/* cap */}
            <rect x="12" y="3" width="8" height="4" rx="1" stroke="currentColor" strokeWidth="1.2" />
            {/* body */}
            <rect x="6" y="7" width="20" height="22" rx="2.5" stroke="currentColor" strokeWidth="1.4" />
            {/* fill */}
            <rect
                x="9"
                y={fillY + 2}
                width="14"
                height={fillH}
                rx="1.5"
                fill={fillColor}
                className={charging ? styles.batteryFillCharging : ""}
            />
            {/* direction indicator */}
            {charging && (
                <path
                    d="M16 13 l-3 4 h2.2 l-1.2 4 4.2-5.5 h-2.2 l1.2-2.5z"
                    fill="#ffffff"
                    stroke="#ffffff"
                    strokeWidth="0.3"
                    strokeLinejoin="round"
                />
            )}
            {discharging && (
                <path
                    d="M16 13 l-3 4 h2.2 l-1.2 4 4.2-5.5 h-2.2 l1.2-2.5z"
                    fill="#ffffff"
                    opacity="0.9"
                    transform="rotate(180 16 18)"
                />
            )}
        </svg>
    );
}

/* ── Node wrapper (button when navigable, div otherwise) ─────────── */
function NodeCard({
    cls,
    active,
    onClick,
    children,
    "aria-label": ariaLabel,
}: {
    cls: string;
    active: boolean;
    onClick?: () => void;
    children: React.ReactNode;
    "aria-label": string;
}) {
    const activeCls = active ? styles.nodeActive : styles.nodeInactive;
    if (onClick) {
        return (
            <button
                type="button"
                className={`${cls} ${activeCls} ${styles.nodeAction}`}
                onClick={onClick}
                aria-label={ariaLabel}
            >
                {children}
            </button>
        );
    }
    return (
        <div className={`${cls} ${activeCls}`} role="group" aria-label={ariaLabel}>
            {children}
        </div>
    );
}

/* ── helpers ─────────────────────────────────────────────────────── */
function fmtPower(w: number): string {
    const abs = Math.abs(w);
    if (abs >= 1000) return (abs / 1000).toFixed(2) + " kW";
    return Math.round(abs) + " W";
}

/** Map power (W) onto a visual stroke width 2…7. Logarithmic for readability. */
function powerToStrokeWidth(w: number): number {
    const abs = Math.abs(w);
    if (abs <= 0) return 1.5;
    // 100 W → ~2.2, 1 kW → ~4, 10 kW → ~5.8, 50 kW → ~7
    const scaled = 1.6 + Math.log10(1 + abs / 50) * 1.6;
    return Math.max(2, Math.min(7, scaled));
}
